import AlertModal from '@/components/modals/AlertModal.vue';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';

export interface AlertButton {
	text: string;
	role?: 'cancel' | 'destructive' | string;
	cssClass?: string | string[];
	handler?: (value?: any) =>
		| boolean
		| void
		| {
				[key: string]: any;
		  };
}

export interface AlertInput {
	type?:
		| 'date'
		| 'email'
		| 'number'
		| 'password'
		| 'search'
		| 'tel'
		| 'text'
		| 'url'
		| 'time'
		| 'week'
		| 'month'
		| 'datetime-local'
		| 'checkbox'
		| 'radio'
		| 'textarea';
	name?: string;
	placeholder?: string;
	value?: any;
	label?: string;
	checked?: boolean;
	disabled?: boolean;
	id?: string;
	handler?: (input: AlertInput) => void;
	min?: string | number;
	max?: string | number;
	cssClass?: string | string[];
	attributes?: any;
	tabindex?: number;
}

export interface ShowAlertPayload {
	header: string;
	subHeader?: string;
	message?: string;
	buttons: string[];
	options?: Object;
	additionalContexts?: SnowplowContext[];
}

export interface ShowPromptPayload {
	header: string;
	subHeader?: string;
	message?: string;
	buttons: AlertButton[];
	options?: Object;
	inputs?: AlertInput[];
}

const showAlert = async (payload: ShowAlertPayload) => {
	await VFMHelper.ensureVfmCss();

	const { additionalContexts = [] } = payload;

	let clickResult: number | null = null;
	const { header, subHeader = '', message = '', buttons, options = {} } = payload;

	const { promise, dismiss } = VFMHelper.open({
		type: 'alert',
		onOpened: () => {
			TrackingHelper.trackEvent(
				'userinteraction',
				{
					action: 'alert_opened',
					nonInteraction: true,
				},
				additionalContexts
			);
		},
		onClosed: () => {
			TrackingHelper.trackEvent(
				'userinteraction',
				{
					action: 'alert_closed',
					label: clickResult + '',
				},
				additionalContexts
			);
		},
		slotComponent: AlertModal,
		slotProps: {
			header,
			subHeader,
			message,
			buttons: buttons.map((button, index) => {
				return {
					text: button,
					cssClass: `alert-message__button-${index}`,
					handler: () => {
						clickResult = index;
						dismiss(index);
					},
				};
			}),
			...options,
		},
	});

	return promise;
};

const promptAlert = async (payload: ShowPromptPayload) => {
	await VFMHelper.ensureVfmCss();

	let clickResult: number | null = null;
	const { header, subHeader = '', inputs, message = '', buttons, options = {} } = payload;

	const { promise, dismiss } = VFMHelper.open({
		type: 'alert',
		onOpened: () => {
			TrackingHelper.trackEvent('userinteraction', {
				action: 'alert_opened',
				nonInteraction: true,
			});
		},
		onClosed: () => {
			TrackingHelper.trackEvent('userinteraction', {
				action: 'alert_closed',
				label: clickResult + '',
			});
		},
		slotComponent: AlertModal,
		slotProps: {
			header,
			subHeader,
			inputs,
			message,
			buttons: buttons.map((button, index) => {
				return {
					...button,
					cssClass: `alert-message__button-${index}`,
					handler: (data: any) => {
						clickResult = index;
						if (button.handler) {
							let hasNotFilledAllInputs = false;
							// Check if user has filled all inputs, and if not, don't remove the prompt.
							Object.values(data).forEach(key => {
								if (key === '') {
									hasNotFilledAllInputs = true;
								}
							});
							if (!hasNotFilledAllInputs) {
								button?.handler(data);
								dismiss(index);
							}
						} else {
							dismiss(index);
						}
					},
				};
			}),
			...options,
		},
	});

	return promise;
};

export { promptAlert, showAlert };
